// Choix faits par la personne pour une prise dont le manifeste ne dit pas tout
// (spec anti-perte phase 1, §5.2.4 et DS §6.11) : prise adoptée depuis des
// fichiers en vrac, restaurée de la corbeille, ou fusionnée sans projet. Le
// natif n'écrit jamais de métadonnées après le démarrage (`markTake` ne porte
// que l'état, `captureId` et les envois) : l'app garde donc ces choix à côté,
// dans Preferences, et l'exécuteur les lit dans chaque manifeste par
// `withTakeOverrides`.
//
// C25 (capture tronquée du 24-09) : le choix porte aussi la capture EXISTANTE
// que la prise complète — l'exécuteur ne crée alors jamais une seconde
// capture ; l'audio du serveur est écrasé (take-runtime.ts).
import type { TakeRecorder } from "../native/take-recorder";
import type { KeyValueStorage } from "./storage";
import { isFeedbackMeta } from "./take-meta";
import type { TakeManifest, TakeMeta } from "./take-types";

export const TAKE_OVERRIDES_KEY = "captureBesoins.takeOverrides";

/** Capture existante que la prise complète (C25) : son audio sera écrasé. */
export interface TakeCompletion {
  captureId: string;
}

export interface TakeOverride {
  takeId: string;
  meta: TakeMeta;
  complete?: TakeCompletion;
  /**
   * Première lecture RÉUSSIE de `listTakes` qui n'a pas vu la prise (ISO). Une
   * seule lecture ne prouve rien (magasin natif momentanément illisible : liste
   * vide) : le choix n'est oublié qu'au passage suivant, si la prise est
   * toujours absente. Effacé dès qu'elle est revue.
   */
  goneSince?: string;
}

export interface TakeOverrideStore {
  list(): Promise<TakeOverride[]>;
  get(takeId: string): Promise<TakeOverride | null>;
  set(o: TakeOverride): Promise<void>;
  remove(takeId: string): Promise<void>;
  /**
   * À appeler une fois par passage, après une lecture réussie de `listTakes`
   * (`live` = prises vues). Un choix n'est oublié que si sa prise est absente
   * de DEUX lectures espacées d'au moins un passage (`goneSince`), et jamais
   * s'il s'agit d'une capture à compléter (C25) : celui-là ne part qu'à la
   * libération ou à la mise à la corbeille de la prise (`withTakeOverrides`) —
   * sinon une lecture vide par intermittence laisserait la prise « sans
   * projet », puis `merged` et cachée pour toujours.
   */
  prune(live: ReadonlySet<string>): Promise<void>;
}

function parseOverrides(raw: string | null): TakeOverride[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (o): o is TakeOverride =>
        typeof o?.takeId === "string" && typeof o?.meta?.userId === "string",
    );
  } catch {
    return [];
  }
}

// Même patron que la mémoire de corbeille (`trash.ts`) : écritures SÉRIALISÉES,
// deux lire-modifier-écrire qui se croisent perdraient un choix.
export function createTakeOverrideStore(
  kv: KeyValueStorage,
  now: () => number = Date.now,
): TakeOverrideStore {
  const read = async () => parseOverrides(await kv.get(TAKE_OVERRIDES_KEY));
  let queue: Promise<unknown> = Promise.resolve();
  function mutate(f: (list: TakeOverride[]) => TakeOverride[]): Promise<void> {
    const next = queue.then(async () => {
      const list = f(await read());
      if (list.length === 0) await kv.remove(TAKE_OVERRIDES_KEY);
      else await kv.set(TAKE_OVERRIDES_KEY, JSON.stringify(list));
    });
    queue = next.catch(() => {});
    return next;
  }
  return {
    list: read,
    get: async (takeId) => (await read()).find((o) => o.takeId === takeId) ?? null,
    set: ({ goneSince: _gone, ...o }) =>
      mutate((l) => [...l.filter((x) => x.takeId !== o.takeId), o]),
    remove: (takeId) => mutate((l) => l.filter((x) => x.takeId !== takeId)),
    prune: (live) =>
      mutate((l) =>
        l.flatMap((o): TakeOverride[] => {
          if (live.has(o.takeId)) {
            const { goneSince: _gone, ...seen } = o;
            return [seen];
          }
          if (o.complete) return [o];
          // Absente pour la 2ᵉ fois, un passage au moins après la 1ʳᵉ : oubliée.
          if (o.goneSince) return [];
          return [{ ...o, goneSince: new Date(now()).toISOString() }];
        }),
      ),
  };
}

/** Durée à partir de laquelle un enregistrement sans métadonnées est une rencontre (R62). */
export const FALLBACK_RENCONTRE_MIN_MS = 5 * 60_000;

/**
 * Type d'un enregistrement sans aucune métadonnée (orphelin sans trace, prise
 * restaurée sans mémoire), déduit de sa durée (décision R62) : 5 min ou plus,
 * une rencontre ; sinon une idée flash.
 */
export function fallbackTakeType(durationMs: number): TakeMeta["type"] {
  return durationMs >= FALLBACK_RENCONTRE_MIN_MS ? "rencontre" : "idee_flash";
}

/**
 * Métadonnées d'une prise dont la personne vient de choisir le projet (C13,
 * DS §6.11). Sans métadonnées : type déduit de la durée, et le mode
 * d'extraction par défaut de l'écran de capture (`readPendingBatchExtraction`
 * sans préférence posée : « pas pressé », `true`) — le même pour les deux types.
 */
export function takeMetaWithProject(
  meta: TakeMeta | null,
  o: { userId: string; startedAt: string; projectId: string; durationMs: number },
): TakeMeta {
  if (meta) return { ...meta, projectId: o.projectId };
  return {
    userId: o.userId,
    type: fallbackTakeType(o.durationMs),
    projectId: o.projectId,
    title: null,
    meetingContext: null,
    batchExtraction: true,
    startedAt: o.startedAt,
  };
}

/**
 * Manifeste vu avec les choix de la personne. Une prise encore `recording`
 * n'en reçoit aucun : une rencontre adoptée serait sinon proposée à la
 * reprise (`offer_resume`) au lieu d'être fermée et fusionnée. Une prise
 * d'avis vocal non plus : un choix ne la change jamais en capture (C30).
 */
export function applyTakeOverride(m: TakeManifest, o: TakeOverride | null): TakeManifest {
  if (!o || m.state === "recording" || isFeedbackMeta(m.meta)) return m;
  // C25 : la capture à compléter l'emporte, même sur un identifiant que
  // l'exécuteur aurait posé avant que le choix soit écrit — sinon une seconde
  // capture serait créée à côté de la capture tronquée.
  const captureId = o.complete?.captureId ?? m.captureId;
  return {
    ...m,
    meta: JSON.stringify(o.meta),
    ...(captureId !== undefined ? { captureId } : {}),
  };
}

/** L'enregistreur des prises, dont chaque manifeste lu porte les choix de la personne. */
export function withTakeOverrides(recorder: TakeRecorder, store: TakeOverrideStore): TakeRecorder {
  const apply = async (m: TakeManifest) => applyTakeOverride(m, await store.get(m.takeId));
  return {
    ...recorder,
    listTakes: async () => {
      const r = await recorder.listTakes();
      const overrides = await store.list();
      const byId = new Map(overrides.map((o) => [o.takeId, o]));
      return {
        ...r,
        takes: r.takes.map((m) => applyTakeOverride(m, byId.get(m.takeId) ?? null)),
      };
    },
    markTake: async (o) => apply(await recorder.markTake(o)),
    // La prise n'existe plus (libérée après confirmation serveur, ou mise à la
    // corbeille) : c'est le SEUL signal sûr pour oublier son choix, même
    // « compléter » (C25). Un refus du natif le garde.
    releaseTake: async (takeId) => {
      await recorder.releaseTake(takeId);
      await store.remove(takeId).catch(() => undefined);
    },
    trashTake: async (takeId) => {
      const r = await recorder.trashTake(takeId);
      await store.remove(takeId).catch(() => undefined);
      return r;
    },
  };
}
